import {useMutation} from "@apollo/client";
import classNames from "classnames";
import {useMemo, useState} from "react";
import {FiExternalLink, FiPlus} from "react-icons/fi";
import {LuChartBar} from "react-icons/lu";
import {Link} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {useFilteredRowSelection} from "@/components/table/hooks/useFilteredRowSelection.ts";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {TableRowDragHandleCell} from "@/components/table/subComponents/TableRowDragHandleCell/TableRowDragHandleCell.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {GQL_STATUS_PAGE_QUERY, GQL_UPDATE_METRICS_ORDER} from "@/lib/queries/statuspages.ts";
import {Metric} from "@/lib/types/statuspages.ts";
import {StatusPageSystemMetricUpdateModal} from "@/pages/statuspages/tabs/systemMetrics/systemMetricModal/StatusPageSystemMetricUpdateModal.tsx";

import {createHandleDragEnd} from "./StatusPageSystemMetricsTable.utils.ts";
import {StatusPageSystemMetricsRowActions} from "./subComponents/StatusPageSystemMetricsRowActions.tsx";
import {TableBulkActionsBar} from "./subComponents/TableBulkActionsBar.tsx";

import styles from "./StatusPageSystemMetricsTable.module.scss";

interface StatusPageSystemMetricsTableV2Props {
  data: {
    data: Metric[];
    total: number;
  };
  dataTestId?: string;
  statusPageId: number;
  isFiltered?: boolean;
}

export const StatusPageSystemMetricsTableV2 = ({
  data,
  dataTestId,
  statusPageId,
  isFiltered,
}: StatusPageSystemMetricsTableV2Props) => {
  const {user} = useCurrentUserContext();
  const {rowSelection, setRowSelection} = useFilteredRowSelection({
    data: data.data,
    getRowId: (row) => row.id.toString(),
  });
  const [metrics, setMetrics] = useState<Metric[]>(data.data);
  const [updateOrder] = useMutation(GQL_UPDATE_METRICS_ORDER, {
    refetchQueries: [GQL_STATUS_PAGE_QUERY],
  });
  const [showMetricUpdateModal, setShowMetricUpdateModal] = useState(false);

  useMemo(() => {
    setMetrics(data.data);
  }, [data.data]);

  const handleDragEnd = useMemo(
    () =>
      createHandleDragEnd({
        metrics,
        statusPageId,
        updateOrder,
        setMetrics,
      }),
    [metrics, statusPageId, updateOrder]
  );

  const specs: ColumnSpec<Metric>[] = [];

  specs.push(
    {
      fieldName: "name",
      fieldTitle: "Name",
      meta: {
        headerClassName: styles.nameCell,
      },
      display: (cellContext) => {
        if (!cellContext) {
          return null;
        }
        const row = cellContext.row;
        const metric = row.original;
        return (
          <div className={classNames("d-flex align-items-center gap-2")}>
            <Tooltip tooltipContent={metric.name} delayMs={500}>
              <div className={classNames("ellipsis-overflow pulseTextBodySmall")}>{metric.name}</div>
            </Tooltip>
            {metric.is_visible && (
              <Tooltip tooltipContent={"Response time metrics will be displayed on the status page."}>
                <Icon icon={LuChartBar} className={"pulse-color-gray-600"} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      fieldName: "source",
      fieldTitle: "Source",
      meta: {
        headerClassName: styles.sourceCell,
      },
      display: (cellContext) => {
        if (!cellContext) {
          return null;
        }
        const metric = cellContext.row.original;
        return (
          <div className={"d-flex"}>
            {metric.service ? (
              <Link
                to={`/checks/${metric.service}`}
                target={"_blank"}
                className={"d-flex align-items-center gap-1 ellipsis-overflow"}
              >
                <span className={"ellipsis-overflow"}>{metric.source}</span>
                <Icon icon={FiExternalLink} />
              </Link>
            ) : (
              <Tooltip tooltipContent={metric.source} delayMs={500}>
                <div className={classNames("ellipsis-overflow")}>{metric.source}</div>
              </Tooltip>
            )}
          </div>
        );
      },
    }
  );

  if (user?.has_write_access) {
    specs.push({
      fieldName: "actions",
      size: 40,
      meta: {
        headerClassName: styles.actionCell,
      },
      accessor: (metric: Metric) => (
        <StatusPageSystemMetricsRowActions key={metric.id} statusPageID={statusPageId} metric={metric} />
      ),
    });
    specs.push({
      fieldName: "id",
      size: 40,
      meta: {
        headerClassName: styles.actionCell,
        columnClassName: styles.actionCell,
      },
      display: (cellContext) => {
        if (!cellContext) {
          return null;
        }
        const row = cellContext.row;
        return (
          <div className={"d-flex align-items-center"}>
            <Tooltip
              tooltipContent={
                isFiltered
                  ? "Clear search to enable reordering."
                  : "Arrange how System Metrics will appear on your Status Page."
              }
            >
              <div>
                <TableRowDragHandleCell rowId={row.id} disabled={isFiltered} />
              </div>
            </Tooltip>
          </div>
        );
      },
    });
  }

  return (
    <>
      <Table
        isDragAndDrop={user?.has_write_access && !isFiltered}
        onDragEnd={handleDragEnd}
        rows={metrics}
        specs={specs}
        initialState={{columnPinning: {right: ["actions", "id"]}}}
        fixedWidth={true}
        minWidth={"330px"}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        tableRowClassName={styles.tableRow}
        actionBar={
          <TableBulkActionsBar
            selectedItems={rowSelection}
            setSelectedItems={setRowSelection}
            statusPageId={statusPageId}
          />
        }
        getRowId={(row) => row.id.toString()}
        noDataComponent={
          <EmptyTable colSpan={specs.length + (rowSelection ? 1 : 0)}>
            {isFiltered ? (
              <div className={"d-flex"}>No System Metrics found matching your search above.</div>
            ) : (
              <div className={"d-flex flex-column align-items-center gap-2 py-5"}>
                <div className={"pulseTextBodyBold"}>You have not yet created any System Metrics</div>
                <div className={"pulse-color-gray-700"}>
                  Add System Metrics, such as response time, for added visibility into critical system performance on
                  your page.
                </div>
                <Button variant={"secondary"} onClick={() => setShowMetricUpdateModal(true)} className={"text-nowrap"}>
                  <Icon icon={FiPlus} />
                  New System Metric
                </Button>
                <StatusPageSystemMetricUpdateModal
                  show={showMetricUpdateModal}
                  setShow={setShowMetricUpdateModal}
                  statusPageID={statusPageId}
                  title="Create System Metric"
                  submitBtnText="Create Metric"
                />
              </div>
            )}
          </EmptyTable>
        }
        dataTestId={dataTestId}
      />
    </>
  );
};
